import React, { useState, useEffect, useMemo } from 'react';
import Papa from 'papaparse';

interface CSVEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  content: string;
  onSave: (content: string) => void;
}

/** W polu w cudzysłowach wewnętrzny cudzysłów musi być zdublowany (""). Naprawia typowy błąd "trailing quote". */
function tryFixQuotedFields(csv: string): string {
  let out = '';
  let inQuoted = false;
  for (let i = 0; i < csv.length; i++) {
    const c = csv[i];
    const next = csv[i + 1];
    if (c === '"') {
      if (inQuoted && next === '"') {
        out += '""';
        i++;
        continue;
      }
      if (inQuoted && next !== undefined && next !== ',' && next !== '\n' && next !== '\r') {
        out += '""';
        continue;
      }
      inQuoted = !inQuoted;
      out += c;
    } else {
      out += c;
    }
  }
  return out;
}

const CSVEditorModal: React.FC<CSVEditorModalProps> = ({
  isOpen,
  onClose,
  content,
  onSave,
}) => {
  const [parseError, setParseError] = useState<string | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [rawFallback, setRawFallback] = useState(content);
  const [useRawMode, setUseRawMode] = useState(false);
  const [draggedRowIndex, setDraggedRowIndex] = useState<number | null>(null);
  const [dragOverRowIndex, setDragOverRowIndex] = useState<number | null>(null);

  const parsed = useMemo(() => {
    if (!content.trim()) {
      return { headers: [], rows: [], error: 'Plik jest pusty.' };
    }
    let toParse = content;
    let result = Papa.parse<Record<string, string>>(toParse, {
      header: true,
      skipEmptyLines: false,
    });
    if (result.errors.length > 0 && result.errors.some(e => /quote|trailing/i.test(e.message))) {
      toParse = tryFixQuotedFields(content);
      result = Papa.parse<Record<string, string>>(toParse, {
        header: true,
        skipEmptyLines: false,
      });
    }
    if (result.errors.length > 0) {
      return {
        headers: result.meta.fields || [],
        rows: result.data || [],
        error: result.errors.map(e => e.message).join('; '),
      };
    }
    const fields = result.meta.fields || (result.data[0] ? Object.keys(result.data[0]) : []);
    return {
      headers: fields,
      rows: result.data || [],
      error: null,
    };
  }, [content]);

  useEffect(() => {
    if (!isOpen) return;
    setParseError(parsed.error);
    setHeaders(parsed.headers);
    setRows(parsed.rows.length > 0 ? parsed.rows : []);
    setRawFallback(content);
    setUseRawMode(false);
    setDraggedRowIndex(null);
    setDragOverRowIndex(null);
  }, [isOpen, content, parsed.error, parsed.headers, parsed.rows]);

  const updateCell = (rowIndex: number, header: string, value: string) => {
    setRows(prev => {
      const next = [...prev];
      if (!next[rowIndex]) next[rowIndex] = {};
      next[rowIndex] = { ...next[rowIndex], [header]: value };
      return next;
    });
  };

  const addRow = () => {
    const newRow: Record<string, string> = {};
    headers.forEach(h => { newRow[h] = ''; });
    setRows(prev => [...prev, newRow]);
  };

  const removeRow = (rowIndex: number) => {
    setRows(prev => prev.filter((_, i) => i !== rowIndex));
  };

  const moveRow = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    setRows(prev => {
      const next = [...prev];
      const [removed] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, removed);
      return next;
    });
  };

  const handleDragStart = (e: React.DragEvent, rowIndex: number) => {
    setDraggedRowIndex(rowIndex);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(rowIndex));
    e.dataTransfer.setDragImage(e.currentTarget as HTMLElement, 0, 0);
  };

  const handleDragOver = (e: React.DragEvent, rowIndex: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedRowIndex !== null && draggedRowIndex !== rowIndex) {
      setDragOverRowIndex(rowIndex);
    }
  };

  const handleDragLeave = () => {
    setDragOverRowIndex(null);
  };

  const handleDrop = (e: React.DragEvent, toIndex: number) => {
    e.preventDefault();
    setDragOverRowIndex(null);
    if (draggedRowIndex === null) return;
    moveRow(draggedRowIndex, toIndex);
    setDraggedRowIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedRowIndex(null);
    setDragOverRowIndex(null);
  };

  const handleSaveTable = () => {
    const csv = Papa.unparse(rows, { columns: headers.length ? headers : undefined });
    onSave(csv);
  };

  const handleSaveRaw = () => {
    onSave(rawFallback);
  };

  const canSaveTable = headers.length > 0 && rows.length >= 0 && !parseError && !useRawMode;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 dark:border-slate-600 flex justify-between items-center flex-shrink-0">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Edycja pliku CSV
          </h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setUseRawMode(!useRawMode)}
              className="text-sm text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
            >
              {useRawMode ? 'Tryb tabeli' : 'Tryb tekstowy'}
            </button>
            <button
              onClick={onClose}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors p-1"
              aria-label="Zamknij"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 flex-grow overflow-auto min-h-0">
          {useRawMode ? (
            <textarea
              value={rawFallback}
              onChange={(e) => setRawFallback(e.target.value)}
              className="w-full h-full min-h-[300px] p-4 font-mono text-sm bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 dark:text-gray-100 rounded-xl focus:ring-2 focus:ring-blue-500 resize-none"
              spellCheck={false}
            />
          ) : parseError ? (
            <div className="space-y-3">
              <p className="text-amber-700 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/30 px-4 py-2 rounded-lg text-sm">{parseError}</p>
              {/quote|trailing|cudzysłów/i.test(parseError) && (
                <p className="text-gray-700 dark:text-gray-300 text-sm bg-blue-50 dark:bg-blue-900/30 px-4 py-2 rounded-lg">
                  W polach ujętych w cudzysłowy (&quot;...&quot;) każdy wewnętrzny cudzysłów zapisz podwójnie: <code className="bg-white dark:bg-slate-700 px-1 rounded">&quot;&quot;</code>. W trybie tekstowym zamień np. <code className="bg-white dark:bg-slate-700 px-1 rounded">&quot;tekst&quot;</code> na <code className="bg-white dark:bg-slate-700 px-1 rounded">&quot;&quot;tekst&quot;&quot;</code> wewnątrz takich pól.
                </p>
              )}
              <p className="text-gray-600 dark:text-gray-400 text-sm">Użyj trybu tekstowego, aby edytować surową zawartość.</p>
              <textarea
                value={rawFallback}
                onChange={(e) => setRawFallback(e.target.value)}
                className="w-full min-h-[300px] p-4 font-mono text-sm bg-gray-50 dark:bg-slate-700 dark:text-gray-100 border border-gray-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-blue-500"
                spellCheck={false}
              />
            </div>
          ) : headers.length === 0 && rows.length === 0 ? (
            <div className="text-gray-500 dark:text-gray-400 py-8 text-center">
              Brak nagłówków lub danych. Użyj trybu tekstowego, aby dodać zawartość.
            </div>
          ) : (
            <>
            <div className="overflow-auto max-h-[50vh] border border-gray-200 dark:border-slate-600 rounded-xl">
              <table className="w-full border-collapse text-sm">
                <thead className="sticky top-0 bg-gray-100 dark:bg-slate-700 z-10">
                  <tr>
                    <th className="sticky left-0 z-20 bg-gray-100 dark:bg-slate-700 border-b border-r border-gray-200 dark:border-slate-600 px-1 py-2 text-left text-gray-600 dark:text-gray-300 font-medium w-10" title="Przeciągnij, aby zmienić kolejność">
                      <span className="sr-only">Kolejność</span>
                    </th>
                    <th className="sticky left-0 z-20 bg-gray-100 dark:bg-slate-700 border-b border-r border-gray-200 dark:border-slate-600 px-2 py-2 text-left text-gray-600 dark:text-gray-300 font-medium w-10">
                      #
                    </th>
                    {headers.map((header) => (
                      <th
                        key={header}
                        className="border-b border-r border-gray-200 dark:border-slate-600 px-2 py-2 text-left text-gray-600 dark:text-gray-300 font-medium whitespace-nowrap min-w-[120px]"
                      >
                        {header}
                      </th>
                    ))}
                    <th className="border-b border-gray-200 dark:border-slate-600 px-2 py-2 w-12 bg-gray-100 dark:bg-slate-700" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      className={`hover:bg-gray-50 dark:hover:bg-slate-700/50 ${draggedRowIndex === rowIndex ? 'opacity-50' : ''} ${dragOverRowIndex === rowIndex ? 'bg-blue-50 dark:bg-blue-900/30 ring-1 ring-blue-200 dark:ring-blue-600' : ''}`}
                      onDragOver={(e) => handleDragOver(e, rowIndex)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, rowIndex)}
                    >
                      <td
                        className="sticky left-0 bg-white dark:bg-slate-800 border-b border-r border-gray-200 dark:border-slate-600 p-0 w-8 cursor-grab active:cursor-grabbing z-10 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        draggable
                        onDragStart={(e) => handleDragStart(e, rowIndex)}
                        onDragEnd={handleDragEnd}
                        title="Przeciągnij, aby zmienić kolejność"
                      >
                        <span className="inline-flex px-1 py-1.5" aria-hidden>
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <circle cx="9" cy="6" r="1.5" />
                            <circle cx="9" cy="12" r="1.5" />
                            <circle cx="9" cy="18" r="1.5" />
                            <circle cx="15" cy="6" r="1.5" />
                            <circle cx="15" cy="12" r="1.5" />
                            <circle cx="15" cy="18" r="1.5" />
                          </svg>
                        </span>
                      </td>
                      <td className="sticky left-8 bg-white dark:bg-slate-800 border-b border-r border-gray-200 dark:border-slate-600 px-2 py-1 text-gray-400 dark:text-gray-500 text-xs z-10 w-8">
                        {rowIndex + 1}
                      </td>
                      {headers.map((header) => (
                        <td key={header} className="border-b border-r border-gray-200 dark:border-slate-600 p-0">
                          <input
                            type="text"
                            value={row[header] ?? ''}
                            onChange={(e) => updateCell(rowIndex, header, e.target.value)}
                            className="w-full min-w-[120px] px-2 py-1.5 border-0 bg-transparent dark:bg-slate-800 dark:text-gray-100 focus:ring-2 focus:ring-blue-400 focus:z-10 rounded"
                          />
                        </td>
                      ))}
                      <td className="border-b border-gray-200 dark:border-slate-600 p-1 bg-white dark:bg-slate-800">
                        <button
                          type="button"
                          onClick={() => removeRow(rowIndex)}
                          className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded"
                          title="Usuń wiersz"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              type="button"
              onClick={addRow}
              className="mt-3 px-4 py-2 text-sm border border-dashed border-gray-300 dark:border-slate-500 text-gray-600 dark:text-gray-300 rounded-lg hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            >
              + Dodaj wiersz
            </button>
            </>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 dark:border-slate-600 flex justify-end gap-4 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-6 py-3 bg-gray-100 dark:bg-slate-600 text-gray-700 dark:text-gray-200 rounded-xl hover:bg-gray-200 dark:hover:bg-slate-500 transition-all duration-200"
          >
            Anuluj
          </button>
          <button
            onClick={useRawMode || parseError ? handleSaveRaw : handleSaveTable}
            disabled={useRawMode ? false : !canSaveTable && !parseError}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-md hover:shadow-xl flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Zapisz zmiany
          </button>
        </div>
      </div>
    </div>
  );
};

export default CSVEditorModal;
